import {useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {DownloadStateTypes, updateOrderStateNew, type OrderDownloadStateNew} from '@Components/download-panel/download-panel.reducer';
import {
  getGenerateDownloadStatusForMultipleOrders,
  getFreebieDownloadStatusForMultipleOrders,
  POLLING_INTERVAL,
  MAX_POLLING_ATTEMPTS,
  filterOrdersByOptions,
  createReadyStateUpdate,
  createErrorStateUpdate,
  createDownloadingStateUpdate,
  triggerBrowserDownload,
} from '@Libraries/poster-download-library';
import {makeFilenameSafe} from '@Utils/file.util';
import {saveToGoogleDriveWithSelectedFolder} from '@Libraries/google-drive-download-picker';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';

export const useGenerateDownloadFromDownloadPage = (): void => {
  const dispatch = useAppDispatch();
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<Record<string, number>>({});
  const downloadTriggeredRef = useRef<Record<string, boolean>>({});
  const isPollingRef = useRef<boolean>(false);

  const orderStateHashmapNew = useAppSelector((state) => state.downloadDialog.orderStateHashmapNew);
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => state.downloadDialog.currentlyProcessingOrderIdKeys);
  const downloadPagePollingTrigger = useAppSelector((state) => state.downloadDialog.downloadPagePollingTrigger);

  const getLatestState = useRef(() => ({
    orderStateHashmapNew,
    currentlyProcessingOrderIdKeys,
  }));

  getLatestState.current = () => ({
    orderStateHashmapNew,
    currentlyProcessingOrderIdKeys,
  });

  const stillDownloadingOrdersRef = useRef<Set<string>>(new Set());

  const getOrdersToPoll = (): OrderDownloadStateNew[] => {
    const {orderStateHashmapNew: currentHashmap, currentlyProcessingOrderIdKeys: currentKeys} = getLatestState.current!();
    const allOrders = currentKeys.map((orderIdKey) => currentHashmap[orderIdKey]).filter((order): order is OrderDownloadStateNew => !!order);

    const downloadingOrders = filterOrdersByOptions(
      allOrders,
      {
        includeDownloadPageOrFreebie: true,
      },
      stillDownloadingOrdersRef.current
    );

    return downloadingOrders.filter((order) => order.state === DownloadStateTypes.DOWNLOADING && order.generationKey);
  };

  const stopPolling = (): void => {
    pollingAttemptsRef.current = {};
    stillDownloadingOrdersRef.current.clear();
    isPollingRef.current = false;
  };

  const scheduleNextPoll = (): void => {
    pollingTimeoutRef.current = setTimeout(() => {
      void handlePollingForDownloadPageOrders();
    }, POLLING_INTERVAL);
  };

  const markOrdersAsTracking = (orders: OrderDownloadStateNew[]): void => {
    orders.forEach((order) => {
      stillDownloadingOrdersRef.current.add(order.orderIdKey);
    });
  };

  const separateOrdersByType = (orders: OrderDownloadStateNew[]) => {
    const freebieOrders = orders.filter((order) => order.isFreebieDownloadPageOrder === true && order.generationKey);
    const regularOrders = orders.filter((order) => order.isFreebieDownloadPageOrder !== true && order.generationKey);
    return {freebieOrders, regularOrders};
  };

  const createOrderMapByGenerationKey = (orders: OrderDownloadStateNew[]): Map<string, OrderDownloadStateNew> => {
    const orderMap = new Map<string, OrderDownloadStateNew>();
    orders.forEach((order) => {
      if (order.generationKey) {
        orderMap.set(order.generationKey, order);
      }
    });
    return orderMap;
  };

  const fetchDownloadStatuses = async (freebieOrders: OrderDownloadStateNew[], regularOrders: OrderDownloadStateNew[]) => {
    const [freebieStatuses, regularStatuses] = await Promise.all([
      freebieOrders.length > 0 ? getFreebieDownloadStatusForMultipleOrders(freebieOrders.map((order) => order.generationKey!)) : Promise.resolve({statuses: {}}),
      regularOrders.length > 0 ? getGenerateDownloadStatusForMultipleOrders(regularOrders.map((order) => order.generationKey!)) : Promise.resolve({statuses: {}}),
    ]);
    return {...freebieStatuses.statuses, ...regularStatuses.statuses};
  };

  const handleOrderError = (order: OrderDownloadStateNew, errorMessage?: string): void => {
    if (errorMessage) {
      console.error(`Error polling for order ${order.orderIdKey}:`, errorMessage);
    }
    const errorUpdate = createErrorStateUpdate({
      orderIdKey: order.orderIdKey,
      orderId: order.orderId,
      name: order.name,
      fileType: order.fileType,
      isDownloadPageOrder: order.isDownloadPageOrder ?? false,
      isFreebieDownloadPageOrder: order.isFreebieDownloadPageOrder ?? false,
      thumbnailUrl: order.thumbnailUrl,
      generationKey: order.generationKey,
    });
    dispatch(
      updateOrderStateNew({
        ...errorUpdate,
        state: DownloadStateTypes.ERROR,
      } as OrderDownloadStateNew)
    );
    delete pollingAttemptsRef.current[order.orderIdKey];
    stillDownloadingOrdersRef.current.delete(order.orderIdKey);
  };

  const triggerDownloadForOrder = (order: OrderDownloadStateNew, downloadUrl: string): void => {
    if (downloadTriggeredRef.current[order.orderIdKey]) {
      return;
    }

    downloadTriggeredRef.current[order.orderIdKey] = true;
    const downloadSafePosterName = makeFilenameSafe(order.name, 'poster');

    if (order.isGoogleDriveDownload) {
      void saveToGoogleDriveWithSelectedFolder(downloadUrl, downloadSafePosterName).then(() => {
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
      });
      return;
    }

    triggerBrowserDownload(downloadUrl, downloadSafePosterName, order.fileType);
  };

  const handleOrderReady = (order: OrderDownloadStateNew, downloadUrl: string): void => {
    triggerDownloadForOrder(order, downloadUrl);

    const readyUpdate = createReadyStateUpdate(
      {
        orderIdKey: order.orderIdKey,
        orderId: order.orderId,
        name: order.name,
        fileType: order.fileType,
        isDownloadPageOrder: order.isDownloadPageOrder ?? false,
        isFreebieDownloadPageOrder: order.isFreebieDownloadPageOrder ?? false,
        thumbnailUrl: order.thumbnailUrl,
        generationKey: order.generationKey,
        isGoogleDriveDownload: order.isGoogleDriveDownload,
      },
      downloadUrl
    );
    dispatch(
      updateOrderStateNew({
        ...readyUpdate,
        state: DownloadStateTypes.READY,
      } as OrderDownloadStateNew)
    );
    delete pollingAttemptsRef.current[order.orderIdKey];
    stillDownloadingOrdersRef.current.delete(order.orderIdKey);
  };

  const handleOrderStillProcessing = (order: OrderDownloadStateNew): void => {
    const downloadingUpdate = createDownloadingStateUpdate(
      {
        orderIdKey: order.orderIdKey,
        orderId: order.orderId,
        name: order.name,
        fileType: order.fileType,
        isDownloadPageOrder: order.isDownloadPageOrder ?? false,
        isFreebieDownloadPageOrder: order.isFreebieDownloadPageOrder ?? false,
        generationKey: order.generationKey,
        thumbnailUrl: order.thumbnailUrl,
        isGoogleDriveDownload: order.isGoogleDriveDownload,
      },
      0
    );
    dispatch(
      updateOrderStateNew({
        ...downloadingUpdate,
        state: DownloadStateTypes.DOWNLOADING,
      } as OrderDownloadStateNew)
    );

    pollingAttemptsRef.current[order.orderIdKey] = (pollingAttemptsRef.current[order.orderIdKey] ?? 0) + 1;

    if (pollingAttemptsRef.current[order.orderIdKey] >= MAX_POLLING_ATTEMPTS) {
      handleOrderError(order);
    }
  };

  const processStatusResults = (orderMap: Map<string, OrderDownloadStateNew>, allStatuses: Record<string, any>): void => {
    for (const [generationKey, status] of Object.entries(allStatuses)) {
      const order = orderMap.get(generationKey);
      if (!order) continue;

      if (status && typeof status === 'object' && !Array.isArray(status)) {
        const statusObj = status as {status?: string; downloadUrl?: string; errorMessage?: string};

        if (statusObj.downloadUrl && statusObj.status) {
          handleOrderReady(order, statusObj.downloadUrl);
          continue;
        }

        if (statusObj.errorMessage && statusObj.status) {
          handleOrderError(order, statusObj.errorMessage);
          continue;
        }
      }

      handleOrderStillProcessing(order);
    }
  };

  const handlePollingForDownloadPageOrders = async (): Promise<void> => {
    isPollingRef.current = true;

    const ordersStillDownloading = getOrdersToPoll();

    if (ordersStillDownloading.length === 0) {
      stopPolling();
      return;
    }

    markOrdersAsTracking(ordersStillDownloading);

    try {
      const {freebieOrders, regularOrders} = separateOrdersByType(ordersStillDownloading);
      const allStatuses = await fetchDownloadStatuses(freebieOrders, regularOrders);
      const orderMap = createOrderMapByGenerationKey(ordersStillDownloading);
      processStatusResults(orderMap, allStatuses);

      if (stillDownloadingOrdersRef.current.size > 0) {
        scheduleNextPoll();
      } else {
        isPollingRef.current = false;
      }
    } catch (error) {
      console.error('Error polling for download page order progress:', error);

      for (const order of ordersStillDownloading) {
        handleOrderError(order);
      }
      stopPolling();
    }
  };

  useEffect(() => {
    const {orderStateHashmapNew: latestHashmap} = getLatestState.current();
    const downloadingOrders = currentlyProcessingOrderIdKeys
      .map((key) => latestHashmap[key])
      .filter((order) => order && (order.isDownloadPageOrder === true || order.isFreebieDownloadPageOrder === true) && order.state === DownloadStateTypes.DOWNLOADING);
    if (downloadingOrders.length > 0 && !isPollingRef.current) {
      void handlePollingForDownloadPageOrders();
    }

    return (): void => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [downloadPagePollingTrigger]);
};
